import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Drawer, ConfigProvider, Spin } from 'antd';
import {
  DashboardOutlined,
  QrcodeOutlined,
  UserOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  LogoutOutlined,
  MenuOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { authService } from './services';
import frFR from 'antd/locale/fr_FR';
import moment from 'moment';
import 'moment/locale/fr';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import './App.css';

moment.locale('fr');
dayjs.locale('fr');

const Login = React.lazy(() => import('./pages/Login'));
const Scanner = React.lazy(() => import('./pages/Scanner'));
const Employes = React.lazy(() => import('./pages/Employes'));
const Sites = React.lazy(() => import('./pages/Sites'));
const Pointages = React.lazy(() => import('./pages/Pointages'));
const MesPointages = React.lazy(() => import('./pages/MesPointages'));
const QuickPointage = React.lazy(() => import('./pages/QuickPointage'));
const AutoInscription = React.lazy(() => import('./pages/AutoInscription'));
const ChangerPin = React.lazy(() => import('./pages/ChangerPin'));
const Profil = React.lazy(() => import('./pages/Profil'));

const { Header, Sider, Content } = Layout;

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <Spin size="large" tip="Chargement..." />
  </div>
);

const AppBrandMark = ({ size = 20 }) => (
  <span className="app-brand-mark" aria-hidden="true">
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  </span>
);

function MainLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [currentUser, setCurrentUser] = React.useState(() => authService.getCurrentUser());

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);

    const handleAuthChange = (e) => {
      const user = e && e.detail !== undefined ? e.detail : authService.getCurrentUser();
      setCurrentUser(user);
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('auth-change', handleAuthChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  const handleLogout = () => {
    authService.logout();
    setCurrentUser(null);
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Mon profil',
      onClick: () => navigate('/profil')
    },
    {
      key: 'change-pin',
      icon: <KeyOutlined />,
      label: 'Changer mon PIN',
      onClick: () => navigate('/changer-pin')
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Déconnexion',
      onClick: handleLogout
    }
  ];

  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  const menuItems = isAdmin ? [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard'
    },
    {
      key: '/scanner',
      icon: <QrcodeOutlined />,
      label: 'Scanner'
    },
    {
      key: '/employes',
      icon: <UserOutlined />,
      label: 'Employés'
    },
    ...((currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER') ? [
      {
        key: '/sites',
        icon: <EnvironmentOutlined />,
        label: 'Sites'
      }
    ] : [])
  ] : [
    {
      key: '/scanner',
      icon: <QrcodeOutlined />,
      label: 'Scanner'
    },
    {
      key: '/mes-pointages',
      icon: <ClockCircleOutlined />,
      label: 'Mes pointages'
    }
  ];

  const mobileMenuItems = [
    ...menuItems,
    { type: 'divider' },
    {
      key: '/profil',
      icon: <UserOutlined />,
      label: 'Mon profil'
    },
    {
      key: '/changer-pin',
      icon: <KeyOutlined />,
      label: 'Changer mon PIN'
    }
  ];

  const getRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN': return 'Administrateur';
      case 'MANAGER': return 'Manager';
      case 'JOURNALIER': return 'Journalier';
      case 'STAGIAIRE': return 'Stagiaire';
      case 'TECHNICIENNE_DE_SURFACE': return 'Technicienne de surface';
      default: return 'Employé';
    }
  };

  const currentPath = location.pathname;

  return (
    <Layout className="app-shell" style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          className="app-sider"
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme="light"
          style={{
            position: 'fixed', left: 0, top: 0, bottom: 0, height: '100vh', zIndex: 101,
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.02)', borderRight: '1px solid #f1f5f9'
          }}
        >
          <div className="app-brand">
            <AppBrandMark />
            {!collapsed && <span className="app-brand-name">MCT Pointage</span>}
          </div>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{ borderRight: 0 }}
          />
        </Sider>
      )}

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AppBrandMark />
            <span style={{ fontWeight: '800', color: '#1e293b' }}>MCT Pointage</span>
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        bodyStyle={{ padding: 0 }}
        width={280}
      >
        <div style={{
          padding: '16px', background: 'linear-gradient(135deg, #4f46e5 0%, #312e81 100%)',
          color: 'white', marginBottom: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar size={48} icon={<UserOutlined />} style={{ background: 'rgba(255,255,255,0.2)' }} />
            <div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>
                {currentUser?.prenom} {currentUser?.nom}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                {getRoleLabel(currentUser?.role)}
              </div>
            </div>
          </div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[currentPath]}
          items={mobileMenuItems}
          onClick={({ key }) => {
            navigate(key);
            setMobileMenuOpen(false);
          }}
          style={{ border: 'none' }}
        />
        <div style={{ padding: '16px', borderTop: '1px solid #f1f5f9', marginTop: 'auto' }}>
          <Button danger icon={<LogoutOutlined />} onClick={handleLogout} block size="large" style={{ borderRadius: '8px', fontWeight: '600' }}>
            Déconnexion
          </Button>
        </div>
      </Drawer>

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 200), transition: 'all 0.2s', minHeight: '100vh' }}>
        <Header className="app-header" style={{ background: '#ffffff', padding: isMobile ? '0 16px' : '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isMobile && <Button type="text" icon={<MenuOutlined />} onClick={() => setMobileMenuOpen(true)} style={{ fontSize: '18px' }} />}
            <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isMobile ? (
                <><AppBrandMark size={18} /><span>MCT Pointage</span></>
              ) : (<span>Portail de présence</span>)}
            </div>
          </div>
          {!isMobile && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="profile-trigger" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '0 14px', height: '38px', borderRadius: '19px', border: '1px solid #e2e8f0', transition: 'all 0.2s', lineHeight: 'normal' }}>
                <Avatar size="small" icon={<UserOutlined />} style={{ background: '#4f46e5' }} />
                <span style={{ fontWeight: '600', color: '#475569', fontSize: '13px' }}>{currentUser?.prenom}</span>
              </div>
            </Dropdown>
          )}
        </Header>
        <Content className="app-content" style={{ margin: isMobile ? '0' : '24px 16px', padding: isMobile ? '0' : 24, background: '#f8fafc', minHeight: 280 }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}

function AppContent() {
  const [currentUser, setCurrentUser] = React.useState(() => authService.getCurrentUser());
  const [authLoading, setAuthLoading] = React.useState(() => {
    return !!localStorage.getItem('token') && !authService.getCurrentUser();
  });

  React.useEffect(() => {
    const handleAuthChange = (e) => {
      const user = e && e.detail !== undefined ? e.detail : authService.getCurrentUser();
      setCurrentUser(user);
      setAuthLoading(false);
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('auth-change', handleAuthChange);

    const token = localStorage.getItem('token');
    if (token) {
      authService.verify()
        .then(res => {
          if (res.valid && res.employe) {
            localStorage.setItem('user', JSON.stringify(res.employe));
            setCurrentUser(res.employe);
          }
        })
        .catch((error) => {
          if ([401, 403].includes(error.response?.status)) {
            authService.logout();
            setCurrentUser(null);
          } else {
            console.error('Vérification de session indisponible, session locale conservée.', error);
          }
        })
        .finally(() => {
          setAuthLoading(false);
        });
    } else {
      setAuthLoading(false);
    }

    return () => {
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <Spin size="large" tip="Chargement..." />
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  const PrivateRoute = ({ children }) => {
    const token = localStorage.getItem('token');
    return token ? children : <Navigate to="/login" replace />;
  };

  const AdminRoute = ({ children }) => {
    const token = localStorage.getItem('token');
    const user = authService.getCurrentUser();
    const isAdminUser = user?.role === 'ADMIN' || user?.role === 'MANAGER';

    if (!token) return <Navigate to="/login" replace />;
    if (!isAdminUser) return <Navigate to="/scanner" replace />;
    return children;
  };

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/inscription" element={<AutoInscription />} />
      <Route path="/*" element={
        <PrivateRoute>
          <MainLayout>
            <Routes>
              <Route path="/" element={isAdmin ? <AdminRoute><Pointages /></AdminRoute> : <Navigate to="/scanner" replace />} />
              <Route path="/scanner" element={<Scanner />} />
              <Route path="/pointage" element={<QuickPointage inLayout={true} />} />
              <Route path="/mes-pointages" element={<MesPointages />} />
              <Route path="/changer-pin" element={<ChangerPin />} />
              <Route path="/profil" element={<Profil />} />
              <Route path="/employes" element={<AdminRoute><Employes /></AdminRoute>} />
              <Route path="/sites" element={<AdminRoute><Sites /></AdminRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </MainLayout>
        </PrivateRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ConfigProvider
      locale={frFR}
      theme={{
        token: {
          colorPrimary: '#4f46e5',
          borderRadius: 8,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          colorBgContainer: '#ffffff',
          colorBgLayout: '#f8fafc',
        },
        components: {
          Card: { borderRadiusLG: 16, boxShadowCard: '0 4px 20px rgba(0, 0, 0, 0.03)' },
          Button: { borderRadius: 8, controlHeight: 38, fontWeight: '600' },
          Table: { borderRadius: 12, colorHeaderBg: '#f8fafc', colorHeaderColor: '#475569' }
        }
      }}
    >
      <Router>
        <React.Suspense fallback={<PageLoader />}>
          <AppContent />
        </React.Suspense>
      </Router>
    </ConfigProvider>
  );
}

export default App;
